"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, type ReactNode } from "react";
import { Gavel, History, LayoutGrid, Users } from "lucide-react";
import { SectionBoundary } from "@/features/home-v2/states/SectionBoundary";
import { NftOffers } from "./NftOffers";
import { NftActivity, NftHolders } from "./NftRecord";
import type { NftView } from "./nftView";
import type { NftFinance } from "./useNftFinance";
export type NftTabKey = "overview" | "offers" | "activity" | "holders";

/** A region that threw keeps the rest of the page alive — the legacy boundaries, one per region. */
export function Region(t0) {
  const $ = _c(5);
  const {
    isFa,
    what,
    children
  } = t0;
  const t1 = isFa ? `${what[0]} بارگذاری نشد` : `${what[1]} didn't load`;
  const t2 = isFa ? "\u0628\u0642\u06CC\u0647\u200C\u06CC \u0627\u06CC\u0646 \u0635\u0641\u062D\u0647 \u0633\u0627\u0644\u0645 \u0627\u0633\u062A." : "The rest of this page is fine.";
  const t3 = isFa ? "\u062A\u0644\u0627\u0634 \u0645\u062C\u062F\u062F" : "Try again";
  let t4;
  if ($[0] !== children || $[1] !== t1 || $[2] !== t2 || $[3] !== t3) {
    t4 = <SectionBoundary title={t1} description={t2} retryLabel={t3}>{children}</SectionBoundary>;
    $[0] = children;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
const ICON: Record<NftTabKey, typeof Gavel> = {
  overview: LayoutGrid,
  offers: Gavel,
  activity: History,
  holders: Users
};

/**
 * Offers, activity and holders under the profile's underlined tabs, each
 * panel its own boundary. With `overview` the first tab is the legacy page's
 * «نمای کلی» (the counter, offers, traits, record, note) in place of offers.
 */
export function NftTabs(t0) {
  const $ = _c(22);
  const {
    v,
    f,
    overview
  } = t0;
  const isFa = v.lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const [tab, setTab] = useState(overview ? "overview" : "offers");
  const tokenId = String(v.finance.tokenId);
  const tabs = [overview ? {
    key: "overview",
    label: t("\u0646\u0645\u0627\u06CC \u06A9\u0644\u06CC", "Overview")
  } : {
    key: "offers",
    label: t("\u0622\u0641\u0631\u0647\u0627", "Offers"),
    n: !f.isOffersLoading && !f.isOffersError ? f.offersTotalCount : undefined
  }, {
    key: "activity",
    label: t("\u0641\u0639\u0627\u0644\u06CC\u062A\u200C\u0647\u0627", "Activity")
  }, {
    key: "holders",
    label: t("\u062F\u0627\u0631\u0646\u062F\u06AF\u0627\u0646", "Holders")
  }];
  let t2;
  if ($[2] !== f || $[3] !== isFa || $[4] !== overview || $[5] !== tab || $[6] !== tokenId || $[7] !== v.contract || $[8] !== v.lang) {
    t2 = tab === "overview" ? overview : tab === "offers" ? <Region isFa={isFa} what={["\u0628\u062E\u0634 \u0622\u0641\u0631\u0647\u0627", "The offers"]}><NftOffers f={f} /></Region> : tab === "activity" ? <Region isFa={isFa} what={["\u0628\u062E\u0634 \u0641\u0639\u0627\u0644\u06CC\u062A\u200C\u0647\u0627", "The activity section"]}><NftActivity contractAddress={v.contract} tokenId={tokenId} lang={v.lang} /></Region> : <Region isFa={isFa} what={["\u0628\u062E\u0634 \u062F\u0627\u0631\u0646\u062F\u06AF\u0627\u0646", "The holders section"]}><NftHolders contractAddress={v.contract} tokenId={tokenId} lang={v.lang} /></Region>;
    $[2] = f;
    $[3] = isFa;
    $[4] = overview;
    $[5] = tab;
    $[6] = tokenId;
    $[7] = v.contract;
    $[8] = v.lang;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const panel = t2;
  const t3 = "v2-ptabs-wrap ns-tabs";
  const t4 = "v2-ptabs";
  const t5 = "tablist";
  let t6;
  if ($[10] !== t) {
    t6 = t("\u0628\u062E\u0634\u200C\u0647\u0627", "Sections");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t8 = tabs.map(t7 => {
    const {
      key,
      label,
      n
    } = t7;
    const Icon = ICON[key];
    return <button key={key} type="button" role="tab" id={`ns-tab-${key}`} aria-selected={tab === key} aria-controls={`ns-panel-${key}`} className="v2-ptab v2-focus" onClick={() => setTab(key)}><Icon aria-hidden={true} /><span>{label}</span>{n ? <b className="ns-tab-n">{nf.format(n)}</b> : null}</button>;
  });
  let t9;
  if ($[12] !== t6 || $[13] !== t8) {
    t9 = <div className={t4} role={t5} aria-label={t6}>{t8}</div>;
    $[12] = t6;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = `ns-panel-${tab}`;
  const t11 = `ns-tab-${tab}`;
  let t12;
  if ($[15] !== panel || $[16] !== t10 || $[17] !== t11) {
    t12 = <div className="v2-ptab-panel" role="tabpanel" id={t10} aria-labelledby={t11}>{panel}</div>;
    $[15] = panel;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== t9) {
    t13 = <div className={t3}>{t9}{t12}</div>;
    $[19] = t12;
    $[20] = t9;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  return t13;
}